/**
 * @file     client.ts
 * @readme   ../CLAUDE.md
 * @purpose  Browser-side Supabase client (dashboard/admin only)
 */

import { createBrowserClient } from '@supabase/ssr';
import type { Database } from './types';
import { ensureWebStorageReadable } from '@/core/supabase/webStorageGuard';

// Singleton on globalThis so that this module and src/core/supabase/client.ts
// (the AuthProvider's dynamic-import client) share a single instance in the
// browser. Multiple instances race for the same auth-token LockManager lock
// and surface as "Lock ... was released because another request stole it"
// runtime errors.
type ClientT = ReturnType<typeof createBrowserClient<Database>>;
const GLOBAL_KEY = '__relta_supabase_browser_client__';

function getOrCreate(): ClientT {
  const g = globalThis as unknown as Record<string, ClientT | undefined>;
  if (g[GLOBAL_KEY]) return g[GLOBAL_KEY] as ClientT;
  ensureWebStorageReadable();
  const client = createBrowserClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        // No `lock` override: the default Web Locks lock is what serializes
        // token refresh ACROSS TABS. Every tab shares one auth cookie, so
        // without it N admin tabs refresh the same token at once, and the first
        // one that gets a non-retryable error (a 500, a 400) calls
        // _removeSession(), deleting the cookie and signing out every tab.
        // auth-js >= 2.105 steals orphaned locks after lockAcquireTimeout, which
        // covers the Strict Mode / HMR case the old no-op lock existed for.
        // 'implicit' instead of the v2 default 'pkce'. Magic-link
        // verification breaks on PKCE whenever the email is opened in a
        // different browser than the one that initiated the sign-in
        // (mobile email apps default to Safari, private mode wipes the
        // verifier, etc). Implicit flow sets cookies directly on the
        // /auth/v1/verify redirect — works from any browser/device.
        flowType: 'implicit',
      },
    },
  );
  g[GLOBAL_KEY] = client;
  return client;
}

export function createSupabaseBrowserClient() {
  return getOrCreate();
}
